import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import { useCreateDialogLookModal } from 'components/create-dialog/CreateDialogModalFrame';
import { Skeleton } from 'components/ui/skeleton';
import { mapInvoiceExpenseToInvoiceExpenseForm } from 'pages/invoices/util/InvoicesPageUtil';
import React, { useCallback, useEffect } from 'react';
import { Control, UseFormGetValues, UseFormHandleSubmit, UseFormReset, UseFormSetValue } from 'react-hook-form';
import { useSelector } from 'store';
import { useUpdateInvoiceExpenseMutation } from 'store/api/invoice-details';
import { ClassifierData } from 'types/classifiers';
import { InvoiceExpense, InvoiceExpenseForm, InvoiceExpenseUpdateDto, InvoiceMatterForm } from 'types/invoice-details';
import { InvoiceData } from 'types/invoices';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { useInvoice } from '../../../../hooks/useInvoice.ts';
import {
    EmptySelection,
    getItemRowState,
    ITEM_CELL,
    ITEM_TOTAL_CELL,
    itemRowClassName,
    itemSelectionCellClassName,
    SelectionLabel,
} from '../InvoiceItemRow';
import { AdvancedBillingDisplay } from './component/AdvancedBillingDisplay';
import { DescriptionDisplay } from './component/DescriptionDisplay';
import { EmployeeDisplay } from './component/EmployeeDisplay';
import { OriginalAmountDisplay } from './component/OriginalAmountDisplay';
import { TakeActionBackDisplay } from './component/TakeActionBackDisplay';
import { TotalAmountDisplay } from './component/TotalAmountDisplay';
import InvoiceExpenseEditModal from './InvoiceExpenseEditModal';

export interface InvoiceExpenseRowProps {
    invoiceExpense: InvoiceExpense;
    expenseForm: InvoiceExpenseForm;
    classifiers: Record<string, ClassifierData>;
    isNew: boolean;
    readonly: boolean;
    index: number;
    invoice: InvoiceData;
    control: Control<InvoiceMatterForm>;
    validateForm: UseFormHandleSubmit<InvoiceMatterForm>;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    reset: UseFormReset<InvoiceMatterForm>;
    handleUpdateInvoiceExpenses: (idList: number[]) => void;
    advancedBillingEnabled: boolean;
    shouldOpenModal?: boolean;
    clearModalRequest?: () => void;
}

/** An expense row's state, its save and its edit dialog, as both designs' rows draw them. */
export const useInvoiceExpenseRow = (props: InvoiceExpenseRowProps) => {
    const {
        invoiceExpense,
        expenseForm,
        isNew,
        index,
        readonly,
        invoice,
        classifiers,
        control,
        validateForm,
        setValue,
        getValues,
        reset,
        shouldOpenModal,
        clearModalRequest,
    } = props;

    const { expense, isRemoved: isRemovedViaCorrection } = invoiceExpense;
    const { openModal, closeModal } = useCreateDialogLookModal();
    const { isCredit, isCorrection: isCorrectionInvoice, isSent, isPaid } = useInvoice(invoice);
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);
    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);

    const watchedInvoiceExpense = expenseForm;
    const editIntentTypeCode = expenseForm?.editIntentTypeCode;
    const invoiceExpenseDate = expenseForm?.expenseDate;
    const invoiceExpenseEmployee = expenseForm?.employee;
    const isNonBillable = expenseForm?.isNonBillable;
    const selected = expenseForm?.selected;

    const [updateInvoiceExpense] = useUpdateInvoiceExpenseMutation();

    const onSubmit = useCallback(async (): Promise<void> => {
        const formData: InvoiceExpenseForm = getValues(`invoiceExpenses.${index}`);
        const data: InvoiceExpenseUpdateDto = {
            ...formData,
            invoiceId: invoice.id,
            expenseTypeCode: formData.expenseType?.value ?? null,
        };
        const resp = await updateInvoiceExpense({ ...data }).unwrap();
        const updated = mapInvoiceExpenseToInvoiceExpenseForm(resp, classifiers, isPaymentCurrencyMode);
        setValue(`invoiceExpenses.${index}`, updated);
    }, [classifiers, getValues, index, invoice.id, setValue, updateInvoiceExpense, isPaymentCurrencyMode]);

    const cancelEditing = useCallback(() => {
        reset();
        closeModal();
    }, [reset, closeModal]);

    const openInvoiceExpenseEditModal = useCallback((): void => {
        openModal(
            <InvoiceExpenseEditModal
                onSubmit={onSubmit}
                validateForm={validateForm}
                onClose={cancelEditing}
                control={control}
                getValues={getValues}
                setValue={setValue}
                index={index}
                invoice={invoice}
                classifiers={classifiers}
                expense={expense}
            />,
            cancelEditing,
            {
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto',
                width: '700px',
            }
        );
    }, [
        openModal,
        onSubmit,
        cancelEditing,
        control,
        validateForm,
        getValues,
        setValue,
        index,
        invoice,
        classifiers,
        expense,
    ]);

    // Auto-open modal when requested after duplication
    useEffect(() => {
        if (shouldOpenModal && clearModalRequest && index >= 0) {
            openInvoiceExpenseEditModal();
            clearModalRequest();
        }
    }, [index, shouldOpenModal, clearModalRequest, openInvoiceExpenseEditModal]);

    const isRowDisabled = editIntentTypeCode !== null || isRemovedViaCorrection;
    const isRemoveActionDisabled = isCorrectionInvoice && (isSent || isPaid);
    const canTakeActionBack = !readonly && !isRemovedViaCorrection;
    const hideSelectionCheckbox = readonly || isRowDisabled || isRemoveActionDisabled;

    const rowState = getItemRowState({
        isSelected: selected,
        isNew,
        isRowDisabled,
        isCorrectionInvoice,
        readonly,
    });

    return {
        isCredit,
        isPaymentCurrencyMode,
        displayCurrency,
        areButtonsDisabled,
        watchedInvoiceExpense,
        editIntentTypeCode,
        invoiceExpenseDate,
        invoiceExpenseEmployee,
        isNonBillable,
        onSubmit,
        openInvoiceExpenseEditModal,
        isRowDisabled,
        canTakeActionBack,
        hideSelectionCheckbox,
        rowState,
    };
};

type Props = InvoiceExpenseRowProps;

const InvoiceExpenseTableItem = React.memo<Props>(
    (props) => {
        const {
            invoiceExpense,
            index,
            readonly,
            invoice,
            classifiers,
            control,
            setValue,
            getValues,
            handleUpdateInvoiceExpenses,
            advancedBillingEnabled,
        } = props;
        const {
            isCredit,
            isPaymentCurrencyMode,
            displayCurrency,
            areButtonsDisabled,
            watchedInvoiceExpense,
            editIntentTypeCode,
            invoiceExpenseDate,
            invoiceExpenseEmployee,
            isNonBillable,
            onSubmit,
            openInvoiceExpenseEditModal,
            isRowDisabled,
            canTakeActionBack,
            hideSelectionCheckbox,
            rowState,
        } = useInvoiceExpenseRow(props);

        if (watchedInvoiceExpense === undefined) {
            return <Skeleton className="bg-grey-700/11 m-4 h-[120px] w-full rounded-none" />;
        }

        return (
            <div className={itemRowClassName(advancedBillingEnabled ? 'expenseAdvanced' : 'basic', rowState)}>
                <div className={itemSelectionCellClassName(readonly)}>
                    {/* htmlFor matches CheckboxInput's inner Checkbox, which gets id={name} */}
                    {hideSelectionCheckbox ? (
                        <EmptySelection />
                    ) : (
                        <SelectionLabel htmlFor={`invoiceExpenses.${index}.selected`}>
                            <CheckboxInput
                                disabled={areButtonsDisabled}
                                control={control}
                                name={`invoiceExpenses.${index}.selected`}
                            />
                        </SelectionLabel>
                    )}
                </div>
                <div className={ITEM_CELL}>
                    {isRowDisabled && canTakeActionBack ? (
                        <TakeActionBackDisplay
                            index={index}
                            invoiceExpense={invoiceExpense}
                            editIntentTypeCode={editIntentTypeCode}
                            setValue={setValue}
                            control={control}
                            handleUpdateInvoiceExpenses={handleUpdateInvoiceExpenses}
                        />
                    ) : null}
                    <EmployeeDisplay
                        employee={invoiceExpenseEmployee}
                        expenseDate={invoiceExpenseDate}
                        classifiers={classifiers}
                        invoiceExpense={invoiceExpense}
                        isNonBillable={isNonBillable}
                        handleOnClickEditButton={openInvoiceExpenseEditModal}
                        hideEditButton={isCredit || readonly || isRowDisabled}
                    />
                    <DescriptionDisplay
                        invoiceExpense={invoiceExpense}
                        readonly={readonly}
                        isRowDisabled={isRowDisabled}
                        control={control}
                        index={index}
                        onSubmit={() => {
                            void onSubmit();
                        }}
                    />
                </div>
                <div className={ITEM_CELL}>
                    <div className="opacity-decreasing-when-disabled flex flex-col">
                        <OriginalAmountDisplay
                            invoiceExpense={invoiceExpense}
                            readonly={readonly}
                            isRowDisabled={isRowDisabled}
                            control={control}
                            index={index}
                            onSubmit={() => {
                                void onSubmit();
                            }}
                            setValue={setValue}
                            advancedBillingEnabled={advancedBillingEnabled}
                            isPaymentCurrencyMode={isPaymentCurrencyMode}
                            displayCurrency={displayCurrency}
                            invoice={invoice}
                        />
                    </div>
                </div>
                {advancedBillingEnabled ? (
                    <div className={ITEM_CELL}>
                        <AdvancedBillingDisplay
                            readonly={readonly}
                            isRowDisabled={isRowDisabled}
                            control={control}
                            index={index}
                            onSubmit={() => {
                                void onSubmit();
                            }}
                            setValue={setValue}
                            getValues={getValues}
                        />
                    </div>
                ) : null}
                <div className={ITEM_TOTAL_CELL}>
                    <TotalAmountDisplay
                        invoiceExpense={invoiceExpense}
                        currency={displayCurrency}
                        isPaymentCurrencyMode={isPaymentCurrencyMode}
                    />
                </div>
            </div>
        );
    },
    (prev, next) => {
        const pf = prev.expenseForm;
        const nf = next.expenseForm;
        return (
            prev.index === next.index &&
            prev.readonly === next.readonly &&
            prev.isNew === next.isNew &&
            prev.advancedBillingEnabled === next.advancedBillingEnabled &&
            prev.shouldOpenModal === next.shouldOpenModal &&
            pf?.employee?.id === nf?.employee?.id &&
            pf?.expenseDate === nf?.expenseDate &&
            pf?.selected === nf?.selected &&
            pf?.editIntentTypeCode === nf?.editIntentTypeCode &&
            pf?.isNonBillable === nf?.isNonBillable &&
            prev.invoiceExpense.expenseAmount === next.invoiceExpense.expenseAmount &&
            prev.invoiceExpense.description === next.invoiceExpense.description &&
            prev.invoiceExpense.calculationLog?.initialAmount === next.invoiceExpense.calculationLog?.initialAmount &&
            prev.invoiceExpense.calculationLog?.finalAmount === next.invoiceExpense.calculationLog?.finalAmount &&
            prev.invoiceExpense.calculationLog?.paymentCurrency?.initialAmount ===
                next.invoiceExpense.calculationLog?.paymentCurrency?.initialAmount &&
            prev.invoiceExpense.calculationLog?.paymentCurrency?.finalAmount ===
                next.invoiceExpense.calculationLog?.paymentCurrency?.finalAmount
        );
    }
);

export default InvoiceExpenseTableItem;
